import React, { useEffect, useState } from "react";
import TeamHeader from "../TeamHeader/TeamHeader";
import MainHub from "../MainHub/MainHub";
import { useLocation } from "react-router-dom";

// --- Interfaces ---
interface MarketingOrg {
  ORG_PLAN: string;
  ORG_INDUSTRY: string;
  ORG_LOCATIONS: string[];
}

const MarketingMain = () => {
  
  // Getting the region from CustomerDescriptionPage.tsx in case the user came from that page after clicking a region or All as default
  const location = useLocation();
  const preselectedRegion = location.state?.preselectedRegion || "All";
  const [selectedRegions, setSelectedRegions] = useState<string[]>([preselectedRegion]);

  const [orgs, setOrgs] = useState<MarketingOrg[]>([]);
  const [loading, setLoading] = useState(true);

  // Fixed plan and industries categories (expandable if needed)
  const PLAN_TYPES = ["Starter", "Pro", "Enterprise"];
  const INDUSTRY_TYPES = ["Wildfire", "Utility", "Other"];


  // Fetch orgs from API
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const response = await fetch("/portal/api/marketing_orgs/", {
          method: "GET",
          credentials: "same-origin",
        });

        if (response.ok) {
          const data = await response.json();

          // Only keep plan + locations
          const trimmed: MarketingOrg[] = data.map((org: any) => ({
            ORG_PLAN: org.ORG_PLAN,
            ORG_INDUSTRY: org.ORG_INDUSTRY,
            ORG_LOCATIONS: org.ORG_LOCATIONS,
          }));

          setOrgs(trimmed);
        } else {
          console.error("Failed to fetch organizations", response.status);
        }
      } catch (error) {
        console.error("Error fetching organizations", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrgs();
  }, []);

  // helper to format region names
  const formatRegionName = (name: string) =>
    name.replace(/^detectors_/, "")
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  // helper to normalize location string
  const normalize = (str: string) => str.replace(/^detectors_/, "").replace(/_/g, "").toLowerCase();

  const getPlanCounts = (region: string) => {
    const counts: Record<string, number> = {};

    orgs.forEach((org) => {
      if (
        region === "All" ||
        org.ORG_LOCATIONS.some(
          (loc) => normalize(loc) === normalize(region)
        )
      ) {
        counts[org.ORG_PLAN] = (counts[org.ORG_PLAN] || 0) + 1;
      }
    });

    PLAN_TYPES.forEach((plan) => {
      if (!(plan in counts)) {
        counts[plan] = 0;
      }
    });

    return counts;
  };

  const getIndustryCounts = (region: string) => {
    const counts: Record<string, number> = {};

    orgs.forEach((org) => {
      if (
        region === "All" ||
        org.ORG_LOCATIONS.some(
          (loc) => normalize(loc) === normalize(region)
        )
      ) {
        counts[org.ORG_INDUSTRY] = (counts[org.ORG_INDUSTRY] || 0) + 1;
      }
    });

    INDUSTRY_TYPES.forEach((industry) => {
      if (!(industry in counts)) {
        counts[industry] = 0;
      }
    });

    return counts;
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="main-hub-wrapper">
      <TeamHeader title="Marketing" />
      <MainHub onRegionChange={setSelectedRegions} />

      <div className="rep-wrapper">
        {/* Plans Report */}
        <div className="main-hub-report">
          <h1>Plans</h1>
          {selectedRegions.map((region) => {
            const planCounts = getPlanCounts(region);

            return (
              <div className="main-hub-lf-card" key={region}>
                <h1>{formatRegionName(region)}</h1>

                <div className="main-hub-lf-stats">
                  {PLAN_TYPES.map((plan) => (
                    <div className="main-hub-lf-stat" key={plan}>
                      <p>{plan}</p>
                      <h2>{planCounts[plan]}</h2>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Industry Report */}
        <div className="main-hub-report">
          <h1>Industry</h1>
          {selectedRegions.map((region) => {
            const industryCounts = getIndustryCounts(region);

            return (
              <div className="main-hub-lf-card" key={region}>
                <h1>{formatRegionName(region)}</h1>

                <div className="main-hub-lf-stats">
                  {INDUSTRY_TYPES.map((industry) => (
                    <div className="main-hub-lf-stat" key={industry}>
                      <p>{industry}</p>
                      <h2>{industryCounts[industry]}</h2>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default MarketingMain;
